import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCopy } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ResellerApi = () => {
  const [loading, setLoading] = useState(true);
  const [apiProfile, setApiProfile] = useState(null);
  const [generatedKey, setGeneratedKey] = useState('');
  const [generating, setGenerating] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const fetchData = async () => {
    try {
      const profileRes = await axios.get(`${API_URL}/reseller/auth/me`, { headers: authHeaders() });
      setApiProfile(profileRes.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load API profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const generateApiKey = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/reseller/auth/generate-key`,
        {},
        { headers: authHeaders() }
      );
      setGeneratedKey(response.data.apiKey || '');
      toast.success('New API key generated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const docsUrl = API_URL.replace(/\/api\/?$/, '/api/docs');

  const configured = !!apiProfile?.apiKey?.configured;
  const prefix = apiProfile?.apiKey?.prefix || '';
  const last4 = apiProfile?.apiKey?.last4 || '';
  const maskedDisplay =
    configured && prefix && last4
      ? `${prefix.slice(0, 8)}…${last4}`
      : configured
        ? `••••••${last4 || ''}`
        : '';
  const tokenDisplay = generatedKey || maskedDisplay || 'Generate a key to get started';
  const canCopyFullKey = Boolean(generatedKey);

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] lg:min-h-[calc(100vh-4rem)] flex flex-col">
        <div className="mx-auto max-w-4xl space-y-6 my-auto w-full">
        <div className="animate-enterUp">
          <h1 className="text-4xl font-light text-white">API Key Generator</h1>
        </div>

        <div
          className="bg-[#FFFFFF21] rounded-2xl border border-[#EBEBEB47] p-5 sm:p-6 animate-enterZoom backdrop-blur-sm"
          style={{ animationDelay: '90ms' }}
        >
          <h2 className="text-center text-2xl font-semibold text-white sm:text-3xl md:text-4xl">
            Your API key
          </h2>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <div className="flex min-h-[48px] min-w-0 flex-1 items-center rounded-xl border border-[#EBEBEB47] bg-[#FFFFFF14] pl-3 pr-1">
              <code
                className={`min-w-0 flex-1 truncate text-left text-sm ${
                  generatedKey ? 'text-emerald-100' : 'text-white/85'
                }`}
                title={generatedKey || maskedDisplay || undefined}
              >
                {tokenDisplay}
              </code>
              <button
                type="button"
                onClick={() => canCopyFullKey && copyText(generatedKey)}
                disabled={!canCopyFullKey}
                title={canCopyFullKey ? 'Copy key' : 'Copy available after you generate a new key'}
                className="ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-blue-100/80 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
              >
                <FiCopy size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={generateApiKey}
              disabled={generating}
              className="rounded-xl px-4 py-3 text-sm font-medium text-white sm:shrink-0 sm:px-5
              bg-[#FFFFFF0F]
              border border-[#EBEBEB47]
              shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
              hover:bg-[rgba(255,255,255,0.10)]
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200"
            >
              {generating ? 'Generating…' : 'Generate / Regenerate'}
            </button>
          </div>

          {generatedKey && (
            <p className="mt-4 text-center text-xs text-emerald-200/90">
              This key is shown only once — copy it now and store it safely.
            </p>
          )}
        </div>

        <div className="rdp-card bg-[#FFFFFF21] border border-[#EBEBEB47] p-5 animate-enterUp" style={{ animationDelay: '160ms' }}>
          <h2 className="text-lg font-semibold text-white">API Documentation</h2>
          <p className="mt-2 text-sm text-blue-100/70">
            Open interactive Swagger docs with Try it out support.
          </p>
          <a
            href={docsUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl mt-5 px-4 py-2 text-sm text-white inline-block 
              bg-[#FFFFFF0F]
              shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
              hover:bg-[rgba(255,255,255,0.10)]
              transition-all duration-200"
          >
            Open API Documentation (Swagger)
          </a>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResellerApi;
